// Copyright 2026 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useChat} from "@ai-sdk/react";
import {DefaultChatTransport} from "ai";
import {
  Alert,
  Avatar,
  Button,
  Empty,
  Input,
  Select,
  Skeleton,
  Spin,
  Tag,
  Tooltip
} from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MessageOutlined,
  PlusOutlined,
  RightOutlined,
  SendOutlined,
  StopOutlined
} from "@ant-design/icons";
import copy from "copy-to-clipboard";
import * as Conf from "./Conf";
import * as Setting from "./Setting";
import * as KmworkAiBackend from "./backend/KmworkAiBackend";
import {
  buildDraftRevisionMessage,
  getApplicationAccessArtifacts,
  getApplyDraftFromText,
  getDraftEntityLabel,
  getDraftRequiredPaths,
  getDraftReviewWarnings,
  getDraftRiskSummary,
  getDraftTargetSummary,
  getDraftValidationIssues,
  getGroupedDraftFields,
  getMaskedDraftJson,
  getMissingDraftRequiredPaths,
  getSafeDraftFieldValue,
  getSafeDraftMessageText,
  parseJsonContent,
  recordAiDraftHistoryEvent
} from "./AiDraftReview";

function normalizeErrorMessage(error) {
  const fallback = error?.data?.msg || error?.data?.message || error?.message || String(error || "");

  try {
    const parsed = JSON.parse(fallback);
    if (parsed?.msg) {
      return parsed.msg;
    }
    if (parsed?.message) {
      return parsed.message;
    }
  } catch {
    // keep original message
  }

  if (fallback.includes("Please login first")) {
    return "当前登录会话已失效，请重新登录后再试。";
  }

  return fallback || "请求失败";
}

function getDisplayName(account) {
  return account?.displayName || account?.name || "You";
}

function getUserKey(account) {
  const owner = account?.owner || "built-in";
  const name = account?.name || account?.id || "guest";
  return `${owner}/${name}`;
}

function getClientChatIdSegment(value) {
  const segment = String(value || "guest")
    .trim()
    .replace(/[^A-Za-z0-9._:-]/g, "_")
    .replace(/^[^A-Za-z0-9]+/, "");
  return segment || "guest";
}

function buildDraftChatId(account) {
  return `kmauth:${getClientChatIdSegment(getUserKey(account))}:${Date.now()}`;
}

function getAiChatApiUrl() {
  return `${Setting.ServerUrl}${Conf.AiChatApi}`;
}

function buildKmAuthAiContext(account) {
  const selectedOrganization = Setting.getOrganization();
  const pathname = window.location.pathname || "";
  return [
    "KMAuth AI 对话规则：",
    "当用户要求新增或编辑 organization、user、application、provider 时，最终确认指令必须返回 type 为 kmauth.applyDraft 的 JSON。",
    "编辑 action=edit 时必须包含 target，用 target 定位现有对象，用 fields 表示要反显的新值。",
    "组织编辑 target 至少包含 owner 和 name；如果用户说“把组织 A 改成 B”或“修改组织名 A 为 B”，target.name 必须是 A，fields.name 必须是 B。",
    "用户编辑 target 至少包含 owner 和 name；应用编辑 target 至少包含 organization 和 name。",
    "Provider 编辑 target 至少包含 owner 和 name；Provider 新增 fields 至少包含 owner、name、category、type。",
    "Provider OIDC/OAuth 自定义配置使用 category=OAuth、type=Custom，可输出 clientId、clientSecret、customAuthUrl、customTokenUrl、customUserInfoUrl、providerUrl、scopes。",
    "Provider Email 配置使用 category=Email，可输出 host、port、clientId、clientSecret、clientId2、clientSecret2、title、content、metadata、receiver。",
    "应用 OAuth/OIDC 接入草稿使用 entity=application，可输出 clientId、clientSecret、redirectUris、grantTypes、tokenFormat、enablePkce；新增应用 fields 至少包含 owner、organization、name。",
    "当用户要求基于上一份草稿修改时，保留原草稿的 entity、action、target，只更新用户明确要求的 fields。",
    "草稿只用于用户确认后反显到页面表单，不要声称已经保存到后端。",
    "不要用 fields 中的新名称作为编辑页面跳转目标。",
    `当前页面：${pathname || "/"}`,
    `当前登录账号：${account?.owner || "built-in"}/${account?.name || account?.id || "guest"}`,
    `当前选择组织：${selectedOrganization || "All"}`,
  ].join("\n");
}

function getMessageText(message) {
  if (!message) {
    return "";
  }

  if (Array.isArray(message.parts)) {
    return message.parts
      .map((part) => {
        if (part?.type === "text" || part?.type === "reasoning") {
          return part.text || "";
        }
        return "";
      })
      .filter(Boolean)
      .join("\n");
  }

  if (typeof message.content === "string") {
    return message.content;
  }

  return "";
}

function renderMessagePart(part, partKey) {
  if (!part) {
    return null;
  }

  if (part.type === "reasoning") {
    return (
      <div className={`kmauth-ai-message-text ${part.type === "reasoning" ? "kmauth-ai-message-text--reasoning" : ""}`} key={partKey}>
        {part.text}
      </div>
    );
  }

  if (part.type === "text") {
    return (
      <div className="kmauth-ai-message-rich-text" key={partKey}>
        {renderStructuredContent(part.text || "", partKey)}
      </div>
    );
  }

  if (part.type === "source-url") {
    return (
      <a className="kmauth-ai-message-source" href={part.url} key={partKey} rel="noreferrer" target="_blank">
        {part.title || part.url}
      </a>
    );
  }

  if (part.type === "source-document" || part.type === "file") {
    return (
      <div className="kmauth-ai-message-attachment" key={partKey}>
        {part.title || part.filename || part.url}
      </div>
    );
  }

  return null;
}

function groupMessageParts(parts) {
  const groups = [];
  let pendingReasoning = [];

  (Array.isArray(parts) ? parts : []).forEach((part, index) => {
    if (!part) {
      return;
    }

    if (part.type === "reasoning") {
      pendingReasoning.push({...part, __index: index});
      return;
    }

    if (pendingReasoning.length > 0) {
      groups.push({
        type: "reasoning",
        parts: pendingReasoning,
        key: `reasoning-${pendingReasoning[0].__index}`,
      });
      pendingReasoning = [];
    }

    groups.push({
      type: "content",
      part,
      key: `content-${index}`,
    });
  });

  if (pendingReasoning.length > 0) {
    groups.push({
      type: "reasoning",
      parts: pendingReasoning,
      key: `reasoning-${pendingReasoning[0].__index}`,
    });
  }

  return groups;
}

function hasRenderableMessageContent(message) {
  if (Array.isArray(message?.parts)) {
    return message.parts.some((part) => {
      if (!part) {
        return false;
      }
      if (part.type === "text" || part.type === "reasoning") {
        return Boolean(part.text);
      }
      if (part.type === "source-url") {
        return Boolean(part.url || part.title);
      }
      if (part.type === "source-document" || part.type === "file") {
        return Boolean(part.title || part.filename || part.url);
      }
      return false;
    });
  }

  return Boolean(getMessageText(message));
}

function renderPendingAssistantContent() {
  return <div className="kmauth-ai-message-text kmauth-ai-message-text--pending">正在生成...</div>;
}

function getApplyDraftFromMessage(message) {
  return getApplyDraftFromText(getMessageText(message));
}

function ApplyDraftCard({draft, onConfirm}) {
  const [showMaskedJson, setShowMaskedJson] = useState(false);
  const [copyStatus, setCopyStatus] = useState("");
  const [serverValidation, setServerValidation] = useState({status: "validating", issues: [], key: ""});
  const groupedFields = getGroupedDraftFields(draft?.fields || {});
  const missingPaths = getMissingDraftRequiredPaths(draft);
  const validationIssues = getDraftValidationIssues(draft);
  const requiredPaths = getDraftRequiredPaths(draft);
  const warningMessages = getDraftReviewWarnings(draft, missingPaths);
  const riskSummary = getDraftRiskSummary(draft);
  const accessArtifacts = getApplicationAccessArtifacts(draft);
  const maskedDraftJson = getMaskedDraftJson(draft);
  const draftValidationKey = useMemo(() => {
    try {
      return JSON.stringify(draft || {});
    } catch {
      return "";
    }
  }, [draft]);
  const serverValidationIssues = serverValidation.status === "invalid" ? serverValidation.issues : [];
  const isServerValidationPending = serverValidation.status === "validating" || serverValidation.key !== draftValidationKey;
  const canConfirm = missingPaths.length === 0 && validationIssues.length === 0 && serverValidationIssues.length === 0 && !isServerValidationPending;
  const filledRequiredCount = requiredPaths.length - missingPaths.length;

  useEffect(() => {
    let disposed = false;

    if (!draftValidationKey || !draft) {
      setServerValidation({status: "idle", issues: [], key: draftValidationKey});
      return () => {
        disposed = true;
      };
    }

    setServerValidation({status: "validating", issues: [], key: draftValidationKey});
    Promise.resolve(KmworkAiBackend.validateDraft(draft))
      .then((response) => {
        if (disposed) {
          return;
        }

        const data = response?.data || response || {};
        const issues = Array.isArray(data.issues) ? data.issues : [];
        setServerValidation({
          status: data.valid === false || issues.length > 0 ? "invalid" : "valid",
          issues,
          key: draftValidationKey,
        });
      })
      .catch(() => {
        if (!disposed) {
          setServerValidation({status: "unavailable", issues: [], key: draftValidationKey});
        }
      });

    return () => {
      disposed = true;
    };
  }, [draftValidationKey]);

  const getServerValidationIssueText = (issue) => {
    if (!issue) {
      return "";
    }
    return [issue.path, issue.message].filter(Boolean).join(": ");
  };

  const handleCopyMaskedJson = () => {
    copy(maskedDraftJson);
    setCopyStatus("已复制脱敏 JSON");
  };

  const handleConfirmDraft = () => {
    if (!canConfirm) {
      return;
    }

    recordAiDraftHistoryEvent("confirm", draft, {source: "assistant-card"});
    Promise.resolve(KmworkAiBackend.recordDraftEvent("confirm", draft, {source: "assistant-card"})).catch(() => {});
    onConfirm?.(draft);
  };

  const confirmButtonText = isServerValidationPending
    ? "服务端预校验中"
    : (canConfirm ? "确认反显到页面表单" : (serverValidationIssues.length > 0 ? "修正服务端校验问题后可确认" : "补齐必填字段后可确认"));

  return (
    <div className="kmauth-ai-draft-card">
      <div className="kmauth-ai-draft-card-header">
        <div>
          <div className="kmauth-ai-draft-card-eyebrow">AI 草稿确认</div>
          <div className="kmauth-ai-draft-card-title">{getDraftEntityLabel(draft.entity)} 配置草稿</div>
        </div>
        <div className="kmauth-ai-draft-card-tags">
          <Tag>{getDraftEntityLabel(draft.entity)}</Tag>
          <Tag>{draft.action || "edit"}</Tag>
          {requiredPaths.length > 0 ? <Tag>{`必填 ${filledRequiredCount}/${requiredPaths.length}`}</Tag> : null}
          <Tag>{riskSummary.label}</Tag>
        </div>
      </div>
      <div className="kmauth-ai-draft-card-target">
        <span className="kmauth-ai-draft-card-label">目标</span>
        <span className="kmauth-ai-draft-card-value">{getDraftTargetSummary(draft)}</span>
      </div>
      {missingPaths.length > 0 ? (
        <div className="kmauth-ai-draft-card-warning">
          缺少必填字段：{missingPaths.join(", ")}
        </div>
      ) : null}
      {validationIssues.length > 0 ? (
        <div className="kmauth-ai-draft-card-warning kmauth-ai-draft-card-warning--error">
          校验未通过：{validationIssues.join(", ")}
        </div>
      ) : null}
      {isServerValidationPending ? (
        <div className="kmauth-ai-draft-card-warning">
          服务端预校验中
        </div>
      ) : null}
      {serverValidation.status === "unavailable" ? (
        <div className="kmauth-ai-draft-card-warning">
          服务端预校验暂不可用，已保留本地校验结果。
        </div>
      ) : null}
      {serverValidationIssues.length > 0 ? (
        <div className="kmauth-ai-draft-card-warning kmauth-ai-draft-card-warning--error">
          <div>服务端校验未通过</div>
          {serverValidationIssues.map((issue) => (
            <div key={`${issue.path}-${issue.message}`}>{getServerValidationIssueText(issue)}</div>
          ))}
        </div>
      ) : null}
      {warningMessages.length > 0 ? (
        <div className="kmauth-ai-draft-review">
          <div className="kmauth-ai-draft-review-title">确认前检查</div>
          {warningMessages.map((warning) => (
            <div className="kmauth-ai-draft-review-item" key={warning}>{warning}</div>
          ))}
        </div>
      ) : null}
      {riskSummary.items.length > 0 ? (
        <div className={`kmauth-ai-draft-risk kmauth-ai-draft-risk--${riskSummary.level}`}>
          <div className="kmauth-ai-draft-risk-title">风险评估：{riskSummary.label}</div>
          {riskSummary.items.slice(0, 5).map((item) => (
            <div className="kmauth-ai-draft-risk-item" key={`${item.field}-${item.message}`}>{item.message}</div>
          ))}
        </div>
      ) : null}
      {accessArtifacts ? (
        <div className="kmauth-ai-draft-artifacts">
          <div className="kmauth-ai-draft-artifacts-title">接入产物</div>
          <div className="kmauth-ai-draft-artifact-list">
            <div className="kmauth-ai-draft-artifact">
              <span className="kmauth-ai-draft-artifact-label">Authorize URL</span>
              <span className="kmauth-ai-draft-artifact-value">{accessArtifacts.authorizeUrl}</span>
            </div>
            <div className="kmauth-ai-draft-artifact">
              <span className="kmauth-ai-draft-artifact-label">Token URL</span>
              <span className="kmauth-ai-draft-artifact-value">{accessArtifacts.tokenUrl}</span>
            </div>
            <div className="kmauth-ai-draft-artifact">
              <span className="kmauth-ai-draft-artifact-label">测试登录 URL</span>
              <span className="kmauth-ai-draft-artifact-value">{accessArtifacts.testLoginUrl}</span>
            </div>
          </div>
          <div className="kmauth-ai-draft-artifact-checklist">
            {accessArtifacts.checklist.map((item) => (
              <div className="kmauth-ai-draft-artifact-check" key={item}>{item}</div>
            ))}
          </div>
        </div>
      ) : null}
      <div className="kmauth-ai-draft-field-groups">
        {groupedFields.map((group) => (
          <div className="kmauth-ai-draft-field-group" key={group.title}>
            <div className="kmauth-ai-draft-field-group-title">{group.title}</div>
            <div className="kmauth-ai-draft-fields">
              {group.fields.map(([key, value]) => (
                <div className="kmauth-ai-draft-field" key={key}>
                  <span className="kmauth-ai-draft-field-key">{key}</span>
                  <span className="kmauth-ai-draft-field-value">{getSafeDraftFieldValue(key, value)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {showMaskedJson ? (
        <pre className="kmauth-ai-draft-json">
          <code>{maskedDraftJson}</code>
        </pre>
      ) : null}
      <div className="kmauth-ai-draft-actions">
        <Button
          icon={showMaskedJson ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          onClick={() => setShowMaskedJson((value) => !value)}
        >
          {showMaskedJson ? "隐藏脱敏 JSON" : "查看脱敏 JSON"}
        </Button>
        <Button icon={<CopyOutlined />} onClick={handleCopyMaskedJson}>
          复制脱敏 JSON
        </Button>
        {copyStatus ? <span className="kmauth-ai-draft-copy-status">{copyStatus}</span> : null}
        <Button
          className="kmauth-ai-draft-confirm"
          disabled={!canConfirm}
          onClick={handleConfirmDraft}
          type="primary"
        >
          {confirmButtonText}
        </Button>
      </div>
    </div>
  );
}

function normalizeDraftTextValue(value) {
  return String(value || "")
    .trim()
    .replace(/^["'“”‘’]+|["'“”‘’。；;,，\s]+$/g, "");
}

function inferOrganizationRenameTarget(text, draft) {
  const source = String(text || "").replace(/\s+/g, " ");
  const renamePatterns = [
    /(?:把|将)?\s*组织(?:名|名称)?\s*(?:从|为|叫|是)?\s*([A-Za-z0-9._:-]+)\s*(?:改成|改为|修改为|变更为|变成|为|到)\s*([A-Za-z0-9._:-]+)/i,
    /(?:把|将)?\s*([A-Za-z0-9._:-]+)\s*(?:组织)?\s*(?:改成|改为|修改为|变更为|变成|为|到)\s*([A-Za-z0-9._:-]+)/i,
  ];

  for (const pattern of renamePatterns) {
    const match = pattern.exec(source);
    const currentName = normalizeDraftTextValue(match?.[1]);
    const nextName = normalizeDraftTextValue(match?.[2]);
    if (currentName && nextName && (!draft?.fields?.name || draft.fields.name === nextName)) {
      return {
        name: currentName,
      };
    }
  }

  return null;
}

function getDraftContextText(messages, assistantIndex) {
  const previousMessages = messages.slice(0, Math.max(assistantIndex, 0));
  return previousMessages
    .filter((item) => item?.role === "user")
    .slice(-3)
    .map((item) => getMessageText(item))
    .filter(Boolean)
    .join("\n");
}

function completeApplyDraftTarget(draft, messages, assistantIndex, account) {
  if (!draft || draft.action !== "edit" || draft.target?.name) {
    return draft;
  }

  if (draft.entity === "organization") {
    const inferredTarget = inferOrganizationRenameTarget(getDraftContextText(messages, assistantIndex), draft);
    if (inferredTarget?.name) {
      return {
        ...draft,
        target: {
          owner: draft.target?.owner || account?.owner || "admin",
          ...(draft.target || {}),
          ...inferredTarget,
        },
      };
    }
  }

  return draft;
}

function getLatestApplyDraft(messages, account) {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role === "user") {
      continue;
    }

    const draft = completeApplyDraftTarget(getApplyDraftFromMessage(message), messages, index, account);
    if (draft) {
      return draft;
    }
  }

  return null;
}

function isDraftRevisionText(text) {
  return /(修改|改成|改为|变更|换成|替换|删除|移除|去掉|补充|新增|加上|不要|revise|change|replace|remove|delete|update|add)/i.test(String(text || ""));
}

function looksLikeMarkdown(text) {
  return /(^|\n)\s{0,3}(#{1,6}\s|[-*+]\s|\d+\.\s|> )|```|\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\((https?:\/\/[^)]+)\)/m.test(String(text || ""));
}

function renderInlineMarkdown(text, keyPrefix) {
  const nodes = [];
  const pattern = /(\[([^\]]+)\]\((https?:\/\/[^)]+)\))|(`([^`]+)`)|(\*\*([^*]+)\*\*)/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    if (match[2] && match[3]) {
      nodes.push(<a className="kmauth-ai-message-source" href={match[3]} key={`${keyPrefix}-${match.index}`} rel="noreferrer" target="_blank">{match[2]}</a>);
    } else if (match[5]) {
      nodes.push(<code className="kmauth-ai-inline-code" key={`${keyPrefix}-${match.index}`}>{match[5]}</code>);
    } else if (match[7]) {
      nodes.push(<strong key={`${keyPrefix}-${match.index}`}>{match[7]}</strong>);
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

function renderMarkdownBlocks(text, keyPrefix) {
  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    const codeMatch = trimmed.match(/^```([\w-]*)\s*$/);
    if (codeMatch) {
      const codeLines = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith("```")) {
        codeLines.push(lines[index]);
        index += 1;
      }
      index += 1;
      blocks.push(
        <div className="kmauth-ai-code-block" key={`${keyPrefix}-code-${index}`}>
          {codeMatch[1] ? <div className="kmauth-ai-code-block-lang">{codeMatch[1]}</div> : null}
          <pre><code>{codeLines.join("\n")}</code></pre>
        </div>
      );
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      blocks.push(
        <div className={`kmauth-ai-markdown-heading kmauth-ai-markdown-heading--h${headingMatch[1].length}`} key={`${keyPrefix}-heading-${index}`}>
          {renderInlineMarkdown(headingMatch[2], `${keyPrefix}-heading-inline-${index}`)}
        </div>
      );
      index += 1;
      continue;
    }

    if (/^[-*+]\s+/.test(trimmed)) {
      const items = [];
      while (index < lines.length && /^[-*+]\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^[-*+]\s+/, ""));
        index += 1;
      }
      blocks.push(
        <ul className="kmauth-ai-markdown-list" key={`${keyPrefix}-ul-${index}`}>
          {items.map((item, itemIndex) => <li key={`${keyPrefix}-ul-item-${itemIndex}`}>{renderInlineMarkdown(item, `${keyPrefix}-ul-inline-${itemIndex}`)}</li>)}
        </ul>
      );
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items = [];
      while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^\d+\.\s+/, ""));
        index += 1;
      }
      blocks.push(
        <ol className="kmauth-ai-markdown-list" key={`${keyPrefix}-ol-${index}`}>
          {items.map((item, itemIndex) => <li key={`${keyPrefix}-ol-item-${itemIndex}`}>{renderInlineMarkdown(item, `${keyPrefix}-ol-inline-${itemIndex}`)}</li>)}
        </ol>
      );
      continue;
    }

    const paragraphLines = [];
    while (index < lines.length) {
      const currentLine = lines[index];
      const currentTrimmed = currentLine.trim();
      if (!currentTrimmed || /^(#{1,6}\s|[-*+]\s|\d+\.\s|```)/.test(currentTrimmed)) {
        break;
      }
      paragraphLines.push(currentLine);
      index += 1;
    }

    blocks.push(
      <div className="kmauth-ai-markdown-paragraph" key={`${keyPrefix}-paragraph-${index}`}>
        {paragraphLines.map((paragraphLine, lineIndex) => (
          <React.Fragment key={`${keyPrefix}-paragraph-line-${lineIndex}`}>
            {lineIndex > 0 ? <br /> : null}
            {renderInlineMarkdown(paragraphLine, `${keyPrefix}-paragraph-inline-${lineIndex}`)}
          </React.Fragment>
        ))}
      </div>
    );
  }

  return blocks;
}

function renderStructuredContent(text, keyPrefix) {
  const safeText = getSafeDraftMessageText(text);
  if (safeText !== String(text || "")) {
    return renderStructuredContent(safeText, `${keyPrefix}-safe`);
  }

  const jsonContent = parseJsonContent(safeText);
  if (jsonContent !== null) {
    return (
      <pre className="kmauth-ai-json-block" key={`${keyPrefix}-json`}>
        <code>{JSON.stringify(jsonContent, null, 2)}</code>
      </pre>
    );
  }

  if (looksLikeMarkdown(safeText)) {
    return renderMarkdownBlocks(safeText, keyPrefix);
  }

  return <div className="kmauth-ai-message-text" key={`${keyPrefix}-plain`}>{safeText}</div>;
}

function renderMessageContent(message, isPending = false, expandedReasoningKeys = [], toggleReasoning = () => {}, forceExpandReasoning = false) {
  if (isPending && !hasRenderableMessageContent(message)) {
    return renderPendingAssistantContent();
  }

  if (Array.isArray(message.parts)) {
    const groups = groupMessageParts(message.parts);
    if (groups.length > 0) {
      return groups.map((group) => {
        if (group.type === "reasoning") {
          const isExpanded = forceExpandReasoning || expandedReasoningKeys.includes(group.key);
          const reasoningText = group.parts.map((part) => part.text || "").filter(Boolean).join("\n");
          return (
            <div className="kmauth-ai-reasoning-block" key={group.key}>
              <button
                className="kmauth-ai-reasoning-toggle"
                onClick={() => toggleReasoning(group.key)}
                type="button"
              >
                <span className="kmauth-ai-reasoning-toggle-copy">思考过程</span>
                <RightOutlined className={`kmauth-ai-reasoning-toggle-icon ${isExpanded ? "is-open" : ""}`} />
              </button>
              {isExpanded ? <div className="kmauth-ai-message-text kmauth-ai-message-text--reasoning">{reasoningText}</div> : null}
            </div>
          );
        }

        return renderMessagePart(group.part, group.key);
      });
    }
  }

  return renderStructuredContent(getMessageText(message), `${message.id || "message"}-fallback`);
}

function normalizeGatewayRole(role) {
  const value = String(role || "").toLowerCase();
  if (value === "user" || value === "assistant" || value === "system") {
    return value;
  }
  if (value === "ai" || value === "model") {
    return "assistant";
  }
  return "assistant";
}

function adaptGatewayMessage(item, fallbackIdPrefix = "msg") {
  if (item?.uiMessage) {
    return {
      ...item.uiMessage,
      id: item.uiMessage.id || `${fallbackIdPrefix}-${item.runId || item.createdAt || Math.random().toString(36).slice(2)}`,
      role: normalizeGatewayRole(item.uiMessage.role || item.role),
    };
  }

  const role = normalizeGatewayRole(item?.role);
  const text = typeof item?.content === "string" ? item.content : "";
  return {
    id: `${fallbackIdPrefix}-${item?.runId || item?.createdAt || Math.random().toString(36).slice(2)}`,
    role,
    parts: text ? [{type: "text", text}] : [],
  };
}

function getSessionPreview(session) {
  const title = (session?.title || "").trim();
  if (title) {
    return title;
  }

  const compatId = session?.id || session?.clientChatId || session?.chatId || "";
  return compatId ? `会话 ${compatId.slice(-6)}` : "未命名会话";
}

function getActiveSessionId(session) {
  return session?.chatId || session?.id || session?.clientChatId || "";
}

function isSessionOwnedByUser(session, userChatPrefixes, userKeys) {
  const prefixes = Array.isArray(userChatPrefixes) ? userChatPrefixes : [userChatPrefixes];
  const keys = Array.isArray(userKeys) ? userKeys : [userKeys];
  const ids = [session?.clientChatId, session?.id, session?.chatId]
    .filter(Boolean)
    .map((value) => String(value));
  if (ids.some((value) => prefixes.some((prefix) => value.startsWith(prefix)))) {
    return true;
  }

  const sessionUserId = session?.userId || session?.metadata?.userId || session?.metadata?.kmauthUserId;
  return keys.includes(sessionUserId);
}

function parseKmworkTime(value) {
  if (!value) {
    return 0;
  }

  const text = String(value).trim();
  const normalized = text.includes("T") ? text : text.replace(" ", "T");
  const parsed = Date.parse(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function getGatewayMessageRoleRank(item) {
  const role = normalizeGatewayRole(item?.uiMessage?.role || item?.role);
  if (role === "user") {
    return 0;
  }
  if (role === "assistant") {
    return 1;
  }
  return 2;
}

function sortGatewayMessages(items) {
  return items
    .map((item, index) => ({item, index}))
    .sort((left, right) => {
      const leftTime = parseKmworkTime(left.item?.createdAt || left.item?.timestamp || left.item?.updatedAt);
      const rightTime = parseKmworkTime(right.item?.createdAt || right.item?.timestamp || right.item?.updatedAt);
      if (leftTime !== rightTime) {
        return leftTime - rightTime;
      }
      const leftRoleRank = getGatewayMessageRoleRank(left.item);
      const rightRoleRank = getGatewayMessageRoleRank(right.item);
      if (leftRoleRank !== rightRoleRank) {
        return leftRoleRank - rightRoleRank;
      }
      return left.index - right.index;
    })
    .map(({item}) => item);
}

function formatUpdatedTime(value) {
  if (!value) {
    return "未开始";
  }

  return Setting.getFormattedDate(value);
}

function normalizeProviderResponseProviders(response) {
  return Array.isArray(response?.all) ? response.all : [];
}

function dedupeItemsByValue(items) {
  const itemMap = new Map();
  items.forEach((item) => {
    if (!item?.value || itemMap.has(item.value)) {
      return;
    }
    itemMap.set(item.value, item);
  });
  return Array.from(itemMap.values());
}

function flattenProviderModels(response) {
  const items = normalizeProviderResponseProviders(response).flatMap((provider) => {
    const models = provider?.models && typeof provider.models === "object" ? Object.values(provider.models) : [];
    return models.map((model) => {
      const providerID = model?.providerID || provider?.id || "";
      const modelID = model?.id || "";
      return {
        ...model,
        id: modelID,
        providerID,
        providerName: provider?.name || provider?.id || providerID,
        providerSourceId: provider?.id || providerID,
        value: `${providerID}:${modelID}`,
      };
    });
  }).filter((item) => item?.id && item?.providerID);

  return dedupeItemsByValue(items);
}

function getDefaultModelValue(response, models) {
  const defaults = response?.default && typeof response.default === "object" ? response.default : {};
  for (const model of models) {
    if (defaults[model.providerID] && defaults[model.providerID] === model.id) {
      return model.value;
    }
  }
  return models[0]?.value || "";
}

function getSelectedModelConfig(models, selectedModel) {
  return models.find((item) => item.value === selectedModel) || null;
}

function renderSelectOption(title, description = "") {
  return (
    <div className="kmauth-ai-select-option">
      <div className="kmauth-ai-select-option-title">{title}</div>
      {description ? <div className="kmauth-ai-select-option-desc">{description}</div> : null}
    </div>
  );
}

export default function AiAssistantChat(props) {
  const {account, onApplyDraft} = props;
  const [input, setInput] = useState("");
  const [localError, setLocalError] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [chatSessions, setChatSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState("");
  const [currentDraftId, setCurrentDraftId] = useState(() => buildDraftChatId(account));
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [expandedReasoningByMessage, setExpandedReasoningByMessage] = useState({});
  const [isReloadingSidebar, setIsReloadingSidebar] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const messagesEndRef = useRef(null);
  const activeSessionIdRef = useRef("");
  const currentDraftIdRef = useRef(currentDraftId);
  const isSendingRef = useRef(false);
  const skipNextHistoryLoadRef = useRef(false);
  const assistantLabel = "KMAuth AI";
  const displayName = getDisplayName(account);
  const isMobile = Setting.isMobile();
  const isSidebarCompact = !isMobile && isSidebarCollapsed;
  const workspaceGridTemplateColumns = isMobile
    ? undefined
    : (isSidebarCompact ? "84px minmax(0, 1fr)" : "clamp(248px, 28vw, 340px) minmax(0, 1fr)");
  const userKey = useMemo(() => getUserKey(account), [account]);
  const metadataUserKeys = useMemo(() => {
    const keys = [userKey];
    if (account?.id && !keys.includes(account.id)) {
      keys.push(account.id);
    }
    return keys;
  }, [account?.id, userKey]);
  const userChatPrefixes = useMemo(() => {
    const clientKeys = [getClientChatIdSegment(userKey)];
    if (account?.id) {
      const accountIdKey = getClientChatIdSegment(account.id);
      if (!clientKeys.includes(accountIdKey)) {
        clientKeys.push(accountIdKey);
      }
    }
    return clientKeys.map((key) => `kmauth:${key}:`);
  }, [account?.id, userKey]);

  const transport = useMemo(() => new DefaultChatTransport({
    api: getAiChatApiUrl(),
    credentials: "include",
    prepareSendMessagesRequest: ({body, id, messages}) => {
      const contextMessage = {
        id: "kmauth-ai-context",
        role: "system",
        parts: [{type: "text", text: buildKmAuthAiContext(account)}],
      };
      return {
        body: {
          body: {
            ...(body?.body || {}),
            service: Conf.AiGatewayService,
          },
          ...(body?.chatId ? {chatId: body.chatId} : {}),
          ...(body?.clientChatId ? {clientChatId: body.clientChatId} : {}),
          ...(!body?.chatId && !body?.clientChatId ? {id: body?.id || id} : {}),
          messages: [contextMessage, ...messages],
        },
      };
    },
  }), [account]);

  const {
    messages,
    sendMessage,
    status,
    stop,
    error,
    setMessages,
    clearError,
  } = useChat({
    id: `kmauth-ai-${userKey}`,
    transport,
    experimental_throttle: 0,
    onError: (nextError) => {
      setLocalError(normalizeErrorMessage(nextError));
    },
    onFinish: () => {
      void refreshSessions({preferCurrent: true, silent: true, selectFirst: false});
    },
  });

  const isBusy = status === "submitted" || status === "streaming";
  const activeSession = useMemo(() => chatSessions.find((item) => getActiveSessionId(item) === activeSessionId) || null, [chatSessions, activeSessionId]);
  const selectedModelConfig = useMemo(() => getSelectedModelConfig(models, selectedModel), [models, selectedModel]);
  const canSend = input.trim() !== "" && !isBusy;

  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);

  useEffect(() => {
    currentDraftIdRef.current = currentDraftId;
  }, [currentDraftId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      block: "end",
      behavior: status === "streaming" ? "auto" : "smooth",
    });
  }, [messages, status]);

  useEffect(() => {
    if (error) {
      setLocalError(normalizeErrorMessage(error));
    }
  }, [error]);

  const refreshSessions = useCallback(async({preferCurrent = false, silent = false, selectFirst = true} = {}) => {
    try {
      if (silent) {
        setIsReloadingSidebar(true);
      } else {
        setHistoryLoading(true);
      }

      const response = await KmworkAiBackend.getChats();
      const items = Array.isArray(response?.items) ? response.items : [];
      const visibleItems = items.filter((item) => isSessionOwnedByUser(item, userChatPrefixes, metadataUserKeys));
      setChatSessions(visibleItems);

      if (visibleItems.length === 0) {
        if (!isSendingRef.current) {
          setActiveSessionId("");
        }
        return;
      }

      const currentId = activeSessionIdRef.current;
      if (preferCurrent && currentId) {
        const exists = visibleItems.some((item) => getActiveSessionId(item) === currentId);
        if (exists) {
          return;
        }
      }

      if (preferCurrent && !currentId) {
        const draftId = currentDraftIdRef.current;
        const matchedDraft = visibleItems.find((item) => item?.clientChatId === draftId || item?.chatId === draftId || item?.id === draftId);
        if (matchedDraft) {
          skipNextHistoryLoadRef.current = true;
          setActiveSessionId(getActiveSessionId(matchedDraft));
          return;
        }
      }

      if (!currentId && selectFirst) {
        setActiveSessionId(getActiveSessionId(visibleItems[0]));
      }
    } catch (e) {
      setLocalError(normalizeErrorMessage(e));
    } finally {
      setHistoryLoading(false);
      setIsReloadingSidebar(false);
    }
  }, [userChatPrefixes, metadataUserKeys]);

  const loadProviders = useCallback(async() => {
    try {
      setProvidersLoading(true);
      const providerResponse = await KmworkAiBackend.getProviders({service: Conf.AiGatewayService});
      const nextModels = flattenProviderModels(providerResponse);
      setModels(nextModels);
      setSelectedModel((currentValue) => nextModels.some((item) => item.value === currentValue) ? currentValue : getDefaultModelValue(providerResponse, nextModels));
    } catch (e) {
      setModels([]);
      setSelectedModel("");
      setLocalError(normalizeErrorMessage(e));
    } finally {
      setProvidersLoading(false);
    }
  }, []);

  useEffect(() => {
    const nextDraftId = buildDraftChatId(account);
    setCurrentDraftId(nextDraftId);
    setActiveSessionId("");
    setChatSessions([]);
    setInput("");
    setLocalError("");
    setModels([]);
    setSelectedModel("");
    setExpandedReasoningByMessage({});
    clearError();
    setMessages([]);

    void Promise.all([
      refreshSessions(),
      loadProviders(),
    ]);
  }, [account, clearError, loadProviders, refreshSessions, setMessages]);

  const loadSessionMessages = useCallback(async(sessionId) => {
    if (!sessionId) {
      setExpandedReasoningByMessage({});
      setMessages([]);
      return;
    }

    try {
      setMessagesLoading(true);
      setLocalError("");
      clearError();
      const response = await KmworkAiBackend.getChatMessages(sessionId);
      const items = Array.isArray(response?.items) ? response.items : [];
      setExpandedReasoningByMessage({});
      setMessages(sortGatewayMessages(items)
        .map((item, index) => adaptGatewayMessage(item, `history-${index}`))
        .filter((item) => item.role !== "system"));
    } catch (e) {
      setLocalError(normalizeErrorMessage(e));
      setExpandedReasoningByMessage({});
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  }, [clearError, setMessages]);

  useEffect(() => {
    if (!activeSessionId) {
      if (!isSendingRef.current) {
        setExpandedReasoningByMessage({});
        setMessages([]);
      }
      return;
    }

    if (skipNextHistoryLoadRef.current) {
      skipNextHistoryLoadRef.current = false;
      return;
    }

    if (isSendingRef.current) {
      return;
    }

    void loadSessionMessages(activeSessionId);
  }, [activeSessionId, loadSessionMessages, setMessages]);

  const resetToDraftChat = useCallback(() => {
    const nextDraftId = buildDraftChatId(account);
    setCurrentDraftId(nextDraftId);
    setActiveSessionId("");
    setExpandedReasoningByMessage({});
    setMessages([]);
    setLocalError("");
    clearError();
  }, [account, clearError, setMessages]);

  async function handleStartNewChat() {
    if (isBusy) {
      return;
    }

    resetToDraftChat();
  }

  function handleModelChange(value) {
    const nextModel = value || "";
    if (nextModel === selectedModel) {
      return;
    }

    setSelectedModel(nextModel);
  }

  const handleToggleReasoning = useCallback((messageId, groupKey) => {
    setExpandedReasoningByMessage((previous) => {
      const currentKeys = Array.isArray(previous[messageId]) ? previous[messageId] : [];
      const nextKeys = currentKeys.includes(groupKey)
        ? currentKeys.filter((item) => item !== groupKey)
        : [...currentKeys, groupKey];

      if (nextKeys.length === 0) {
        const {[messageId]: _removed, ...rest} = previous;
        return rest;
      }

      return {
        ...previous,
        [messageId]: nextKeys,
      };
    });
  }, []);

  async function handleDeleteChat(session, event) {
    event?.stopPropagation();
    const sessionId = getActiveSessionId(session);
    if (!sessionId) {
      return;
    }

    try {
      await KmworkAiBackend.deleteChat(sessionId);
      if (activeSessionIdRef.current === sessionId) {
        setActiveSessionId("");
        setMessages([]);
      }
      await refreshSessions();
    } catch (e) {
      setLocalError(normalizeErrorMessage(e));
    }
  }

  async function handleStopSession() {
    if (!activeSessionIdRef.current) {
      stop();
      return;
    }

    try {
      await KmworkAiBackend.stopChat(activeSessionIdRef.current);
    } catch (e) {
      setLocalError(normalizeErrorMessage(e));
    } finally {
      stop();
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const text = input.trim();
    if (!text || isBusy) {
      return;
    }
    const revisionDraft = isDraftRevisionText(text) ? getLatestApplyDraft(messages, account) : null;
    const outgoingText = revisionDraft ? buildDraftRevisionMessage(text, revisionDraft) : text;

    const targetChatId = activeSession?.chatId || currentDraftIdRef.current;
    const nextBody = {
      ...(activeSession?.chatId ? {chatId: activeSession.chatId} : {clientChatId: targetChatId}),
      body: {
        service: Conf.AiGatewayService,
        ...(selectedModelConfig ? {model: {providerID: selectedModelConfig.providerID, id: selectedModelConfig.id}} : {}),
      },
    };

    setInput("");
    setLocalError("");
    clearError();
    isSendingRef.current = true;

    try {
      await sendMessage({text: outgoingText}, {body: nextBody});
      await refreshSessions({preferCurrent: true, silent: true, selectFirst: false});
    } catch (e) {
      setLocalError(normalizeErrorMessage(e));
    } finally {
      isSendingRef.current = false;
    }
  }

  const modelOptions = models.map((item) => ({
    value: item.value,
    label: item.name || item.id,
    description: item.providerName ? `${item.providerName} / ${item.id}` : item.id,
    searchText: `${item.name || ""} ${item.id || ""} ${item.providerName || ""}`.toLowerCase(),
  }));

  const filterSelectOption = (inputValue, option) => {
    const searchText = String(option?.searchText || option?.label || "").toLowerCase();
    return searchText.includes(String(inputValue || "").toLowerCase());
  };

  return (
    <div
      className={`kmauth-ai-workspace ${isSidebarCompact ? "kmauth-ai-workspace--sidebar-collapsed" : ""}`}
      style={workspaceGridTemplateColumns ? {gridTemplateColumns: workspaceGridTemplateColumns} : undefined}
    >
      <aside className={`kmauth-ai-sidebar ${isSidebarCompact ? "is-collapsed" : ""}`}>
        <div className="kmauth-ai-sidebar-header">
          <Avatar className="kmauth-ai-sidebar-avatar" style={{backgroundColor: Setting.getAvatarColor(account?.name || "AI")}} size={40}>
            {Setting.getShortName(displayName || "AI")}
          </Avatar>
          {isSidebarCompact ? null : (
            <div className="kmauth-ai-sidebar-copy">
              <div className="kmauth-ai-sidebar-title">KMAuth AI</div>
              <div className="kmauth-ai-sidebar-subtitle">{displayName}</div>
            </div>
          )}
          {isMobile ? null : (
            <Button
              aria-label={isSidebarCompact ? "展开会话侧栏" : "折叠会话侧栏"}
              className="kmauth-ai-sidebar-toggle"
              icon={isSidebarCompact ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setIsSidebarCollapsed((value) => !value)}
              size="small"
              type="text"
            />
          )}
        </div>

        <Button block className="kmauth-ai-new-chat" icon={<PlusOutlined />} onClick={handleStartNewChat} type="primary">
          {isSidebarCompact ? null : "新建会话"}
        </Button>

        {isSidebarCompact ? null : (
          <div className="kmauth-ai-sidebar-section">
            <div className="kmauth-ai-sidebar-section-title">历史会话</div>
            <div className="kmauth-ai-session-list">
              {historyLoading ? (
                <div className="kmauth-ai-session-loading">
                  <Skeleton active paragraph={{rows: 5}} title={false} />
                </div>
              ) : chatSessions.length === 0 ? (
                <Empty description="暂无历史记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : chatSessions.map((session) => {
                const sessionId = getActiveSessionId(session);
                const isActive = sessionId === activeSessionId;

                return (
                  <div
                    className={`kmauth-ai-session-item ${isActive ? "is-active" : ""}`}
                    key={sessionId}
                    onClick={() => {
                      if (isBusy || sessionId === activeSessionId) {
                        return;
                      }
                      setActiveSessionId(sessionId);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (!isBusy && sessionId !== activeSessionId) {
                          setActiveSessionId(sessionId);
                        }
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="kmauth-ai-session-item-main">
                      <div className="kmauth-ai-session-item-title">{getSessionPreview(session)}</div>
                      <div className="kmauth-ai-session-item-meta">{formatUpdatedTime(session.updatedAt || session.createdAt)}</div>
                    </div>
                    <Tooltip title="删除会话">
                      <span className="kmauth-ai-session-item-action">
                        <Button
                          aria-label="删除会话"
                          icon={<DeleteOutlined />}
                          onClick={(event) => {
                            void handleDeleteChat(session, event);
                          }}
                          size="small"
                          type="text"
                        />
                      </span>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </aside>

      <main className="kmauth-ai-main">
        <div className="kmauth-ai-toolbar">
          <div className="kmauth-ai-toolbar-copy">
            <div className="kmauth-ai-toolbar-title">{activeSession ? getSessionPreview(activeSession) : "新建会话"}</div>
            <div className="kmauth-ai-toolbar-meta">
              <span>服务: {Conf.AiGatewayService}</span>
              <span>更新时间: {activeSession ? formatUpdatedTime(activeSession.updatedAt || activeSession.createdAt) : "未开始"}</span>
              <span>切换模型对后续消息生效</span>
              {isReloadingSidebar ? <span>同步中...</span> : null}
            </div>
          </div>

          <div className="kmauth-ai-toolbar-controls">
            <div className="kmauth-ai-toolbar-control">
              <span className="kmauth-ai-toolbar-label">模型</span>
              <Select
                className="kmauth-ai-toolbar-select"
                disabled={isBusy}
                filterOption={filterSelectOption}
                loading={providersLoading}
                onChange={handleModelChange}
                optionRender={(option) => renderSelectOption(option.data.label, option.data.description)}
                options={modelOptions}
                placeholder="选择模型"
                popupMatchSelectWidth={false}
                showSearch
                value={selectedModel || undefined}
              />
            </div>
          </div>
        </div>

        <div className="kmauth-ai-body">
          {messagesLoading ? (
            <div className="kmauth-ai-loading-panel">
              <Spin />
            </div>
          ) : messages.length === 0 ? (
            <div className="kmauth-ai-empty-state">
              <div className="kmauth-ai-empty-icon">
                <MessageOutlined />
              </div>
              <div className="kmauth-ai-empty-title">开始一个新的 AI 调试会话</div>
              <div className="kmauth-ai-empty-subtitle">
                历史记录按当前 KMAuth 用户隔离。模型来自 KMAuth AI 网关 provider。
              </div>
              <div className="kmauth-ai-empty-tags">
                {selectedModelConfig ? <Tag>{selectedModelConfig.name || selectedModelConfig.id}</Tag> : null}
              </div>
            </div>
          ) : (
            <div className="kmauth-ai-messages kmauth-ai-messages--workspace">
              {messages.map((message, index) => {
                const isUser = message.role === "user";
                const name = isUser ? displayName : assistantLabel;
                const isPendingAssistant = !isUser && isBusy && index === messages.length - 1;
                const expandedReasoningKeys = expandedReasoningByMessage[message.id] || [];

                const applyDraft = !isUser ? completeApplyDraftTarget(getApplyDraftFromMessage(message), messages, index, account) : null;

                return (
                  <div className={`kmauth-ai-message-row ${isUser ? "kmauth-ai-message-row--user" : "kmauth-ai-message-row--assistant"}`} key={message.id}>
                    <div className="kmauth-ai-message-meta">{name}</div>
                    <div className="kmauth-ai-message-bubble">
                      {renderMessageContent(message, isPendingAssistant, expandedReasoningKeys, (groupKey) => handleToggleReasoning(message.id, groupKey), isPendingAssistant)}
                      {applyDraft ? <ApplyDraftCard draft={applyDraft} onConfirm={onApplyDraft} /> : null}
                    </div>
                  </div>
                );
              })}
              {isBusy && messages[messages.length - 1]?.role !== "assistant" ? (
                <div className="kmauth-ai-message-row kmauth-ai-message-row--assistant" key="assistant-pending">
                  <div className="kmauth-ai-message-meta">{assistantLabel}</div>
                  <div className="kmauth-ai-message-bubble">
                    {renderPendingAssistantContent()}
                  </div>
                </div>
              ) : null}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {localError ? (
          <Alert
            className="kmauth-ai-error"
            closable
            message={localError}
            onClose={() => {
              setLocalError("");
              clearError();
            }}
            showIcon
            type="error"
          />
        ) : null}

        <form className="kmauth-ai-editor-shell" onSubmit={handleSubmit}>
          <Input.TextArea
            autoSize={{minRows: 4, maxRows: 8}}
            disabled={isBusy}
            onChange={(event) => setInput(event.target.value)}
            onPressEnter={(event) => {
              if (!event.shiftKey) {
                handleSubmit(event);
              }
            }}
            placeholder="输入消息，开始调用 KMAuth AI 对话"
            value={input}
          />
          <div className="kmauth-ai-editor-footer">
            <div className="kmauth-ai-editor-hints">
              <span>当前用户: {displayName}</span>
              {selectedModelConfig ? <span>模型: {selectedModelConfig.name || selectedModelConfig.id}</span> : null}
            </div>
            <div className="kmauth-ai-editor-actions">
              {isBusy ? (
                <Button icon={<StopOutlined />} onClick={handleStopSession}>
                  停止
                </Button>
              ) : (
                <Button disabled={!canSend} htmlType="submit" icon={<SendOutlined />} type="primary">
                  发送
                </Button>
              )}
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
